import React, { useState, useEffect } from "react";
import {
  Container,
  Spinner,
  Alert,
  Button,
  Nav,
  Navbar,
} from "react-bootstrap";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { auth } from "../../firebase";
import Login from "./Login";
import PropertyManagement from "./PropertyManagement";
import BlogManagement from "./BlogManagement";
import ContentManagement from "./ContentManagement"; // New Component
import TeamManagement from "./TeamManagement";
import {
  propertiesService,
  blogService,
} from "../../services/firebaseServices";

const AdminApp = () => {
  const [user, setUser] = useState(null);
  const [activeTab, setActiveTab] = useState("properties");
  const [loading, setLoading] = useState(true);

  // Data States
  const [properties, setProperties] = useState([]);
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      if (currentUser) fetchData();
      else setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  const fetchData = async () => {
    try {
      const [propsData, postsData] = await Promise.all([
        propertiesService.getAll(),
        blogService.getAll(),
      ]);
      setProperties(propsData);
      setPosts(postsData);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Property CRUD operations
  const handleAddProperty = async (propertyData) => {
    try {
      const newProperty = await propertiesService.add(propertyData);
      setProperties((prev) => [newProperty, ...prev]);
      return newProperty;
    } catch (err) {
      console.error("Error adding property:", err);
      throw new Error("Failed to add property");
    }
  };

  const handleUpdateProperty = async (id, propertyData) => {
    try {
      const updatedProperty = await propertiesService.update(id, propertyData);
      setProperties((prev) =>
        prev.map((p) => (p.id === id ? updatedProperty : p))
      );
      return updatedProperty;
    } catch (err) {
      console.error("Error updating property:", err);
      throw new Error("Failed to update property");
    }
  };

  const handleDeleteProperty = async (id) => {
    try {
      await propertiesService.delete(id);
      setProperties((prev) => prev.filter((p) => p.id !== id));
      // Also remove related expenses
    } catch (err) {
      console.error("Error deleting property:", err);
      throw new Error("Failed to delete property");
    }
  };
  // ... CRUD Handlers (handleAddProperty, etc.) would go here, imported from your existing code ...

  if (!user && !loading) return <Login onLogin={setUser} />;
  if (loading)
    return (
      <div className="text-center mt-5">
        <Spinner animation="border" />
      </div>
    );

  return (
    <div className="min-vh-100 bg-light admin-panel">
      <Navbar bg="dark" variant="dark" expand="lg" className="mb-4">
        <Container>
          <Navbar.Brand>ACF Admin</Navbar.Brand>
          <Nav className="me-auto">
            <Nav.Link
              active={activeTab === "properties"}
              onClick={() => setActiveTab("properties")}
            >
              Properties
            </Nav.Link>
            <Nav.Link
              active={activeTab === "blog"}
              onClick={() => setActiveTab("blog")}
            >
              Blog
            </Nav.Link>
            <Nav.Link
              active={activeTab === "content"}
              onClick={() => setActiveTab("content")}
            >
              Site Content
            </Nav.Link>
            <Nav.Link
              active={activeTab === "team"}
              onClick={() => setActiveTab("team")}
            >
              Meet the Team
            </Nav.Link>
          </Nav>
          <Button variant="outline-light" onClick={() => signOut(auth)}>
            Logout
          </Button>
        </Container>
      </Navbar>

      <Container>
        {activeTab === "properties" && (
          <PropertyManagement
            properties={properties}
            onAddProperty={handleAddProperty}
            onUpdateProperty={handleUpdateProperty}
            onDeleteProperty={handleDeleteProperty}
            // Pass your CRUD handlers here from the previous implementation
          />
        )}
        {activeTab === "blog" && (
          <BlogManagement
            posts={posts}
            // Pass your CRUD handlers here
          />
        )}
        {activeTab === "content" && <ContentManagement />}
        {activeTab === "team" && <TeamManagement />}
      </Container>
    </div>
  );
};

export default AdminApp;
